import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { IconComponent } from '../../../../shared/icons/icon.component';
import {
  ContextMenuComponent,
  ContextMenuData,
} from '../../../../shared/components/context-menu/context-menu.component';
import { TranslatePipe } from '@ngx-translate/core';
import {
  ApiDeviceGroupResponseDto,
  DeviceGroupState,
} from '@birco/rainmesh-api';
import { Router } from '@angular/router';
import { RouteConstants } from '../../../../shared/constants/route-constants';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { LabelConverter } from '../../../../shared/converter/label.converter';
import { isDeviceActive } from '../../utils/device-value.utils';
import { Store } from '@ngrx/store';
import { BehaviorSubject, map, of, switchMap } from 'rxjs';
import { DeviceResponseDto } from '../../models/device-response.interface';
import { selectSortedDevicesByGroupId } from '../../state/device-groups/device-group.selectors';

@Component({
  selector: 'tr[app-area-item-row]',
  imports: [
    IconComponent,
    ContextMenuComponent,
    TranslatePipe,
    TagComponent,
    AsyncPipe,
  ],
  templateUrl: './area-item-row.component.html',
  styleUrl: './area-item-row.component.scss',
})
export class AreaItemRowComponent {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly deviceGroupSubject =
    new BehaviorSubject<ApiDeviceGroupResponseDto | null>(null);
  protected readonly DeviceGroupState = DeviceGroupState;
  protected readonly LabelConverter = LabelConverter;

  @Input() isHeader = false;
  @Input() isExpanded = false;
  @Input() set deviceGroup(value: ApiDeviceGroupResponseDto | undefined) {
    this._deviceGroup = value;
    this.deviceGroupSubject.next(value ?? null);
  }
  get deviceGroup(): ApiDeviceGroupResponseDto | undefined {
    return this._deviceGroup;
  }
  private _deviceGroup?: ApiDeviceGroupResponseDto;
  @Output() isExpandedChange = new EventEmitter<boolean>();
  @Output() triggerAction = new EventEmitter<{ id: string; value: boolean }>();
  @Output() edit = new EventEmitter<string>();
  @Output() delete = new EventEmitter<string>();

  readonly deviceGroupView$ = this.deviceGroupSubject.pipe(
    switchMap((deviceGroup) => {
      if (!deviceGroup) {
        return of(null);
      }

      return this.store
        .select(selectSortedDevicesByGroupId(deviceGroup.id))
        .pipe(
          map((devices: DeviceResponseDto[]) => ({
            deviceGroup,
            devices,
            deviceCount: devices.length,
            activeDeviceCount: devices.filter((device) =>
              isDeviceActive(device),
            ).length,
          })),
        );
    }),
  );

  contextMenu: ContextMenuData[] = [
    {
      labelKey: 'default.details',
      action: () => this.onDetails(),
    },
    {
      labelKey: 'deviceGroups.trigger.active',
      action: () => this.onTriggerAction(true),
    },
    {
      labelKey: 'deviceGroups.trigger.inactive',
      action: () => this.onTriggerAction(false),
    },
    {
      labelKey: 'default.edit',
      action: () => this.onEdit(),
    },
    {
      labelKey: 'default.delete',
      action: () => this.onDelete(),
    },
  ];

  onToggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    this.isExpandedChange.emit(this.isExpanded);
  }

  onTriggerAction(value: boolean) {
    if (!this.deviceGroup?.id) return;
    this.triggerAction.emit({
      id: this.deviceGroup.id,
      value: value,
    });
  }

  onEdit() {
    if (!this.deviceGroup?.id) return;
    this.edit.emit(this.deviceGroup.id);
  }

  onDelete() {
    if (!this.deviceGroup?.id) return;
    this.delete.emit(this.deviceGroup.id);
  }

  onDetails() {
    if (!this.deviceGroup?.id) return;
    void this.router.navigate([
      RouteConstants.routeDeviceGroup,
      this.deviceGroup.id,
    ]);
  }
}
